import { useState, useCallback, useRef } from 'react';
import type { ReactFlowEdge, ReactFlowNode } from './types';
import { serializeWorkflow, deserializeWorkflow } from './serialization';

const MAX_HISTORY_SIZE = 50;

interface UndoRedoResult {
  nodes: ReactFlowNode[];
  edges: ReactFlowEdge[];
}

interface UndoRedoState {
  /** Whether undo is available */
  canUndo: boolean;
  /** Whether redo is available */
  canRedo: boolean;
  /** Save current state as a checkpoint (call BEFORE making changes) */
  saveCheckpoint: (nodes: ReactFlowNode[], edges: ReactFlowEdge[]) => void;
  /**
   * Discard the most recent checkpoint without restoring state.
   *
   * Use when a pending edit session is cancelled and its pre-edit checkpoint
   * would otherwise be a no-op undo. Never touches the redo stack. Safe to
   * call when the undo stack is empty.
   */
  popCheckpoint: () => void;
  /** Undo to previous state. Pass current state, get previous state back. Returns null if nothing to undo. */
  undo: (
    currentNodes: ReactFlowNode[],
    currentEdges: ReactFlowEdge[]
  ) => UndoRedoResult | null;
  /** Redo to next state. Pass current state, get next state back. Returns null if nothing to redo. */
  redo: (
    currentNodes: ReactFlowNode[],
    currentEdges: ReactFlowEdge[]
  ) => UndoRedoResult | null;
  /** Clear all undo/redo history (e.g., when switching workflows) */
  clearHistory: () => void;
}

/**
 * Hook for managing undo/redo state in the Logiblocks workflow editor.
 *
 * Uses serialized protobuf binary (Uint8Array) for checkpoint storage.
 * This ensures true deep copies - mutations to the graph after checkpointing
 * won't affect stored checkpoints.
 *
 * Note: Returned nodes have placeholder positions (0,0). The caller should
 * apply calculateGraphLayout() after restoring.
 */
export function useUndoRedo(): UndoRedoState {
  // Use refs for stacks to avoid re-renders on every checkpoint save
  // Storing Uint8Array ensures deep copies (protobuf encode/decode)
  const undoStackRef = useRef<Uint8Array[]>([]);
  const redoStackRef = useRef<Uint8Array[]>([]);

  // State for canUndo/canRedo to trigger re-renders when availability changes
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  const updateFlags = useCallback(() => {
    setCanUndo(undoStackRef.current.length > 0);
    setCanRedo(redoStackRef.current.length > 0);
  }, []);

  const saveCheckpoint = useCallback(
    (nodes: ReactFlowNode[], edges: ReactFlowEdge[]) => {
      const checkpoint = serializeWorkflow(nodes, edges);
      undoStackRef.current.push(checkpoint);

      // Enforce max history size
      if (undoStackRef.current.length > MAX_HISTORY_SIZE) {
        undoStackRef.current.shift(); // Remove oldest
      }

      // Clear redo stack on new edit
      redoStackRef.current = [];

      updateFlags();
    },
    [updateFlags]
  );

  const undo = useCallback(
    (
      currentNodes: ReactFlowNode[],
      currentEdges: ReactFlowEdge[]
    ): UndoRedoResult | null => {
      if (undoStackRef.current.length === 0) {
        return null;
      }

      // Push current state to redo stack
      const currentState = serializeWorkflow(currentNodes, currentEdges);
      redoStackRef.current.push(currentState);

      // Pop previous state from undo stack
      const previousState = undoStackRef.current.pop()!;
      updateFlags();

      return deserializeWorkflow(previousState);
    },
    [updateFlags]
  );

  const redo = useCallback(
    (
      currentNodes: ReactFlowNode[],
      currentEdges: ReactFlowEdge[]
    ): UndoRedoResult | null => {
      if (redoStackRef.current.length === 0) {
        return null;
      }

      // Push current state to undo stack
      const currentState = serializeWorkflow(currentNodes, currentEdges);
      undoStackRef.current.push(currentState);

      // Pop next state from redo stack
      const nextState = redoStackRef.current.pop()!;
      updateFlags();

      return deserializeWorkflow(nextState);
    },
    [updateFlags]
  );

  const popCheckpoint = useCallback(() => {
    if (undoStackRef.current.length === 0) {
      return;
    }
    undoStackRef.current.pop();
    updateFlags();
  }, [updateFlags]);

  const clearHistory = useCallback(() => {
    undoStackRef.current = [];
    redoStackRef.current = [];
    updateFlags();
  }, [updateFlags]);

  return {
    canUndo,
    canRedo,
    saveCheckpoint,
    popCheckpoint,
    undo,
    redo,
    clearHistory,
  };
}
